import * as React from 'react';
import { ContextualMenu, createTheme, DirectionalHint, type IContextualMenuItem, ThemeProvider } from '@fluentui/react';
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { msalInstance } from '../../lib/msal';
import { COMPUTE_TABS, computePath } from './computeRoutes';
import type { ComputeDashboardCatalogItem } from './dashboardTypes';
import { loadComputeDashboards } from './dashboardApi';
import '../../styles/SpoComputeApp.css';

const spoComputeTheme = createTheme({
    palette: {
        themePrimary: '#0f766e',
        themeLighterAlt: '#f1faf9',
        themeLighter: '#ccede9',
        themeLight: '#a3ddd6',
        themeTertiary: '#55bcb0',
        themeSecondary: '#1f8a7f',
        themeDarkAlt: '#0d6b63',
        themeDark: '#0b5a54',
        themeDarker: '#08433e',
    },
});

export function SpoComputeLayout() {
    const { search } = useLocation();
    const [dashboards, setDashboards] = React.useState<ComputeDashboardCatalogItem[]>([]);
    const groupedDashboards = dashboards.filter(isGroupedDashboard);
    const otherDashboards = dashboards.filter(dashboard => !isGroupedDashboard(dashboard));

    React.useEffect(() => {
        const controller = new AbortController();
        loadComputeDashboards(controller.signal)
            .then(setDashboards)
            .catch(() => setDashboards([]));
        return () => controller.abort();
    }, []);

    return (
        <ThemeProvider theme={spoComputeTheme} className="spo-compute-app">
            <header className="spo-compute-header">
                <div className="spo-compute-header-inner">
                    <NavLink to={computePath('cores-per-kdau', search)} className="spo-compute-brand">
                        <span className="spo-compute-logo" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                        </span>
                        <span className="spo-compute-brand-text">
                            <strong>SPO Compute</strong>
                            <small>Insights</small>
                        </span>
                    </NavLink>
                    <nav className="spo-compute-nav" aria-label="SPO Compute reports">
                        {COMPUTE_TABS.map(tab => (
                            <NavLink
                                key={tab.kind}
                                to={computePath(tab.kind, search)}
                                className={({ isActive }) => isActive ? 'spo-compute-nav-link active' : 'spo-compute-nav-link'}
                            >
                                {tab.label}
                            </NavLink>
                        ))}
                        {otherDashboards.map(dashboard => (
                            <NavLink
                                key={dashboard.id}
                                to={`/spocompute/d/${encodeURIComponent(dashboard.id)}${search}`}
                                title="Defined by a dashboard config file"
                                className={({ isActive }) => isActive ? 'spo-compute-nav-link config active' : 'spo-compute-nav-link config'}
                            >
                                {dashboard.title}
                            </NavLink>
                        ))}
                        {MENU_GROUPS.map(group => {
                            const members = groupedDashboards.filter(dashboard => dashboard.id.startsWith(group.idPrefix));
                            return members.length > 0 && <GroupMenu key={group.key} group={group} dashboards={members} />;
                        })}
                    </nav>
                    <AccountMenu />
                </div>
            </header>
            <Outlet />
        </ThemeProvider>
    );
}

function isGroupedDashboard(dashboard: ComputeDashboardCatalogItem): boolean {
    return MENU_GROUPS.some(group => dashboard.id.startsWith(group.idPrefix));
}

// Dashboards whose id starts with a group prefix share one header dropdown. Each item's label drops the title prefix that the
// group repeats, so "Peak RPS by Region" reads "Region - RPS" and "Latency: SQL Query - CPU" reads "SQL Query - CPU".
// Items are listed rule by rule, so all Cores views come before RPS and all CPU views before Time.
interface MenuLabelRule { prefix: string; end?: string; suffix: string }

const MENU_GROUPS: readonly { key: string; label: string; idPrefix: string; labels: readonly MenuLabelRule[] }[] = [
    {
        key: 'peak', label: 'Peak', idPrefix: 'peak-',
        labels: [{ prefix: 'Peak Cores by ', suffix: ' - Cores' }, { prefix: 'Peak RPS by ', suffix: ' - RPS' }],
    },
    {
        key: 'latency', label: 'Latency', idPrefix: 'latency-',
        labels: [
            { prefix: 'Latency: ', end: ' - CPU', suffix: ' - CPU' },
            { prefix: 'Latency: ', end: ' - Time', suffix: ' - Time' },
        ],
    },
];

function labelRule(title: string, labels: readonly MenuLabelRule[]): number {
    return labels.findIndex(rule => title.startsWith(rule.prefix) && title.endsWith(rule.end ?? ''));
}

function menuLabel(title: string, labels: readonly MenuLabelRule[]): string {
    const rule = labels[labelRule(title, labels)];
    if (!rule) return title;
    const core = title.slice(rule.prefix.length, title.length - (rule.end?.length ?? 0)).trim();
    return `${core}${rule.suffix}`;
}

// Unmatched titles keep their catalog position after the matched ones.
function byLabelRule(dashboards: ComputeDashboardCatalogItem[], labels: readonly MenuLabelRule[]): ComputeDashboardCatalogItem[] {
    const rank = (dashboard: ComputeDashboardCatalogItem) => {
        const index = labelRule(dashboard.title, labels);
        return index < 0 ? labels.length : index;
    };
    return [...dashboards].sort((left, right) => rank(left) - rank(right));
}

function GroupMenu({ group, dashboards }: { group: typeof MENU_GROUPS[number]; dashboards: ComputeDashboardCatalogItem[] }) {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const match = useMatch('/spocompute/d/:dashboardId');
    const current = dashboards.find(dashboard => dashboard.id === match?.params.dashboardId);
    const menuId = React.useId();
    // The menu belongs to the page it was opened on, so any navigation closes it without a reset effect.
    const pageKey = `${location.pathname}${location.search}`;
    const [openOn, setOpenOn] = React.useState<string | null>(null);
    const open = openOn === pageKey;
    const setOpen = (value: boolean) => setOpenOn(value ? pageKey : null);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={`spo-compute-nav-link config spo-compute-group-button${current ? ' active' : ''}`}
                title={current?.title ?? `${group.label} dashboards`}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                onClick={() => setOpen(!open)}
                onKeyDown={event => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                {group.label}
                <span className="spo-compute-account-chevron" aria-hidden="true" />
            </button>
            {open && (
                <ContextualMenu
                    id={menuId}
                    ariaLabel={`${group.label} dashboards`}
                    target={buttonRef.current}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    shouldFocusOnMount
                    calloutProps={{ className: 'spo-compute-group-menu' }}
                    onDismiss={() => setOpen(false)}
                    items={byLabelRule(dashboards, group.labels).map((dashboard): IContextualMenuItem => {
                        const path = `/spocompute/d/${encodeURIComponent(dashboard.id)}${location.search}`;
                        return {
                            key: dashboard.id,
                            text: menuLabel(dashboard.title, group.labels),
                            // Screen readers and tooltips keep the full title for context.
                            'aria-label': dashboard.title,
                            title: dashboard.title,
                            href: path,
                            className: dashboard.id === current?.id ? 'is-current' : undefined,
                            'aria-current': dashboard.id === current?.id ? 'page' : undefined,
                            onClick: event => {
                                if (event?.ctrlKey || event?.metaKey || event?.shiftKey || event?.altKey) return;
                                event?.preventDefault();
                                navigate(path);
                                setOpen(false);
                            },
                        };
                    })}
                />
            )}
        </>
    );
}

function AccountMenu() {
    const [open, setOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();
    const user = (window.user ?? undefined) as SignedInUser | undefined;
    const initials = getInitials(user);
    const firstName = user?.name?.trim().split(/\s+/)[0];

    React.useEffect(() => {
        if (!open) {
            return;
        }

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!menuRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
        }

        event.preventDefault();
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
        const current = items.indexOf(document.activeElement as HTMLElement);
        const next = event.key === 'ArrowDown'
            ? (current + 1) % items.length
            : (current - 1 + items.length) % items.length;
        items[next]?.focus();
    };

    return (
        <div className="spo-compute-account" ref={menuRef}>
            <button
                ref={buttonRef}
                type="button"
                className="spo-compute-account-button"
                aria-label="Menu"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(value => !value)}
            >
                <span className="spo-compute-avatar" aria-hidden="true">{initials}</span>
                <span className="spo-compute-account-label">{firstName ?? 'Menu'}</span>
                <span className="spo-compute-account-chevron" aria-hidden="true" />
            </button>
            {open && (
                <div className="spo-compute-account-menu" role="menu" aria-label="SPO Compute menu" onKeyDown={moveFocus}>
                    {(user?.name || user?.emailAddress) && (
                        <div className="spo-compute-account-profile">
                            <span className="spo-compute-avatar large" aria-hidden="true">{initials}</span>
                            <span>
                                {user?.name && <strong>{user.name}</strong>}
                                {user?.emailAddress && <small>{user.emailAddress}</small>}
                            </span>
                        </div>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        aria-label="PR Guardian"
                        onClick={() => {
                            setOpen(false);
                            navigate('/');
                        }}
                    >
                        <span className="spo-compute-menu-icon" aria-hidden="true">
                            <svg viewBox="0 0 20 20" focusable="false">
                                <path d="M10 2.5 3.5 5v4.6c0 4 2.8 6.9 6.5 7.9 3.7-1 6.5-3.9 6.5-7.9V5L10 2.5Z" />
                                <path d="m7.2 10 2 2 3.6-3.8" />
                            </svg>
                        </span>
                        <span className="spo-compute-menu-text">
                            <strong>PR Guardian</strong>
                        </span>
                    </button>
                    <div className="spo-compute-account-divider" role="separator" />
                    <button
                        type="button"
                        role="menuitem"
                        aria-label="Sign out"
                        className="danger"
                        onClick={() => {
                            setOpen(false);
                            void msalInstance.logoutRedirect();
                        }}
                    >
                        <span className="spo-compute-menu-icon" aria-hidden="true">
                            <svg viewBox="0 0 20 20" focusable="false">
                                <path d="M8 3.5H4.5v13H8" />
                                <path d="M12.5 6.5 16 10l-3.5 3.5" />
                                <path d="M16 10H7.5" />
                            </svg>
                        </span>
                        <span className="spo-compute-menu-text">
                            <strong>Sign out</strong>
                        </span>
                    </button>
                </div>
            )}
        </div>
    );
}

type SignedInUser = { name?: string; emailAddress?: string };

function getInitials(user: SignedInUser | undefined): string {
    const source = user?.name?.trim() || user?.emailAddress?.split('@')[0] || '';
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const letters = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : source.slice(0, 2);
    return letters.toUpperCase() || '?';
}
